import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { AddressFields } from '@/components/GooglePlacesAutocomplete';
import { ChargeBreakdown } from '@/components/ChargeBreakdown';
import { DocumentUploadPending } from '@/components/DocumentUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { STEAMSHIP_LINES } from '@/constants/steamshipLines';
import { RAIL_RAMPS, EMPTY_DEPOTS } from '@/constants/columbusTerminals';
import { CONTAINER_SIZES } from '@/constants/containerTypes';
import { OVERWEIGHT_THRESHOLD, MAX_CONTAINERS, validateContainerNumber, APPOINTMENT_TYPES } from '@/constants/commercialLogic';
import { Plus, Trash2, AlertTriangle, Package, Calendar, MapPin, FileText, Truck, Paperclip } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const defaultContainer = () => ({
  id: Date.now(),
  container_number: '',
  container_size: "40' Dry",
  weight_lbs: '',
  hazmat: false,
  chassis_required: false,
  containerNumberError: ''
});

export default function ImportForm({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [loadQuantity, setLoadQuantity] = useState('Single');
  const [containers, setContainers] = useState([defaultContainer()]);
  
  // Load Identifier Section
  const [loadIdentifiers, setLoadIdentifiers] = useState({
    reference_number: '',
    hbl_mbl: '',
    pickup_number: ''
  });
  
  const [commonDetails, setCommonDetails] = useState({
    commodity: '',
    steamship_line: '',
    rail_ramp: '',
    empty_depot: '',
    custom_depot_name: '',
    custom_depot_address: '',
    last_free_day: '',
    special_instructions: ''
  });
  
  // Consignee & Delivery Address (grouped together for Import)
  const [consignee, setConsignee] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [deliveryAddress, setDeliveryAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
    latitude: null,
    longitude: null
  });
  
  // Appointment Scheduling
  const [appointmentDetails, setAppointmentDetails] = useState({
    appointment_type: 'Live Unload',
    drop_date: '',
    pickup_date: '',
    delivery_appointment: ''
  });
  
  // Document upload
  const [pendingDocuments, setPendingDocuments] = useState([]);
  
  // Rate calculation state
  const [rateData, setRateData] = useState(null);
  const [showChargeDetails, setShowChargeDetails] = useState(false);
  const [calculatingRate, setCalculatingRate] = useState(false);

  // Update containers when load quantity changes
  useEffect(() => {
    if (loadQuantity === 'Single' && containers.length > 1) {
      setContainers([containers[0]]);
    }
  }, [loadQuantity]);

  // Calculate rate when address and ramp are set
  useEffect(() => {
    const calculateDistance = async () => {
      if (commonDetails.rail_ramp && deliveryAddress.city && deliveryAddress.zip) {
        setCalculatingRate(true);
        try {
          const response = await axios.post(`${BACKEND_URL}/api/rate/calculate`, {
            ramp: commonDetails.rail_ramp,
            city: deliveryAddress.city,
            zip: deliveryAddress.zip
          });
          setRateData(response.data);
        } catch (err) {
          console.error('Rate calculation error:', err);
          setRateData(null);
        } finally {
          setCalculatingRate(false);
        }
      } else {
        setRateData(null);
      }
    };
    
    const debounce = setTimeout(calculateDistance, 500);
    return () => clearTimeout(debounce);
  }, [commonDetails.rail_ramp, deliveryAddress.city, deliveryAddress.zip]);

  // Add container
  const addContainer = () => {
    if (containers.length < MAX_CONTAINERS) {
      setContainers([...containers, defaultContainer()]);
    }
  };

  // Remove container
  const removeContainer = (index) => {
    if (containers.length > 1) {
      setContainers(containers.filter((_, i) => i !== index));
    }
  };

  // Update container with validation
  const updateContainer = (index, field, value) => {
    const newContainers = [...containers];
    newContainers[index] = { ...newContainers[index], [field]: value };
    
    // Validate container number on change
    if (field === 'container_number' && value) {
      const validation = validateContainerNumber(value);
      newContainers[index].containerNumberError = validation.valid ? '' : validation.message;
    }
    
    setContainers(newContainers);
  };

  // Handle address change
  const handleAddressChange = (addressData) => {
    setDeliveryAddress({
      street: addressData.street || deliveryAddress.street,
      city: addressData.city || deliveryAddress.city,
      state: addressData.state || deliveryAddress.state,
      zip: addressData.zip || deliveryAddress.zip,
      latitude: addressData.latitude || deliveryAddress.latitude,
      longitude: addressData.longitude || deliveryAddress.longitude
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate container numbers
    let hasErrors = false;
    const validatedContainers = containers.map(c => {
      if (c.container_number) {
        const validation = validateContainerNumber(c.container_number);
        if (!validation.valid) {
          hasErrors = true;
          return { ...c, containerNumberError: validation.message };
        }
      }
      return c;
    });
    
    if (hasErrors) {
      setContainers(validatedContainers);
      toast.error('Please fix container number format errors');
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const payload = {
        load_type: 'import',
        load_quantity: loadQuantity,
        number_of_containers: containers.length,
        containers: containers.map(c => ({
          container_number: c.container_number,
          container_size: c.container_size,
          weight_lbs: parseInt(c.weight_lbs) || 0,
          hazmat: c.hazmat,
          chassis_required: c.chassis_required,
          is_overweight: parseInt(c.weight_lbs) >= OVERWEIGHT_THRESHOLD
        })),
        // Load identifiers
        reference_number: loadIdentifiers.reference_number || null,
        hbl_mbl: loadIdentifiers.hbl_mbl,
        pickup_number: loadIdentifiers.pickup_number || null,
        // Shipment details
        commodity: commonDetails.commodity,
        steamship_line: commonDetails.steamship_line,
        rail_ramp: commonDetails.rail_ramp,
        empty_return_depot: commonDetails.empty_depot === 'Other (Enter custom depot below)' 
          ? `${commonDetails.custom_depot_name} - ${commonDetails.custom_depot_address}`
          : commonDetails.empty_depot,
        // Consignee info
        consignee_name: consignee.name,
        consignee_phone: consignee.phone,
        consignee_email: consignee.email,
        // Delivery address
        delivery_address: deliveryAddress,
        // Appointment scheduling
        delivery_type: appointmentDetails.appointment_type,
        drop_off_date: appointmentDetails.drop_date || null,
        pickup_date: appointmentDetails.pickup_date || null,
        delivery_appointment: appointmentDetails.delivery_appointment || null,
        last_free_day: commonDetails.last_free_day || null,
        special_instructions: commonDetails.special_instructions,
        // Rate calculation data
        driving_distance_miles: rateData?.distance_miles,
        zone_classification: rateData?.zone,
        calculated_linehaul: rateData?.line_haul,
        status: rateData?.zone === 'C' ? 'Rate Requested' : 'Submitted'
      };

      const response = await axios.post(`${BACKEND_URL}/api/loads/import`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const loadId = response.data.id;
      
      // Upload pending documents
      if (pendingDocuments.length > 0 && loadId) {
        for (const file of pendingDocuments) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(`${BACKEND_URL}/api/loads/${loadId}/documents`, formData, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            });
          } catch (docErr) {
            console.error('Document upload error:', docErr);
          }
        }
      }

      toast.success('Import load submitted successfully!');
      navigate('/my-shipments');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit load');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      <Navbar user={user} />
      
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid="import-form-page">
        <h1 className="text-3xl md:text-5xl font-bold tracking-tighter uppercase mb-8">
          <span className="text-[#00A6E8]">IMPORT</span>
          <span className="text-white"> DO/WO</span>
        </h1>
        
        {!user?.has_rate_agreement && (
          <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-4 mb-8">
            <p className="text-yellow-400 text-sm">
              <strong>Note:</strong> You do not have an active rate agreement. Rates shown are estimates.
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="import-form">
          
          {/* LOAD IDENTIFIER SECTION */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-6">
            <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
              <FileText className="text-[#00A6E8]" size={20} />
              Load Identifier
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label className="text-slate-300">Reference Number</Label>
                <Input
                  value={loadIdentifiers.reference_number}
                  onChange={(e) => setLoadIdentifiers({...loadIdentifiers, reference_number: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  placeholder="Your reference #"
                  data-testid="reference-number-input"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">HBL / MBL *</Label>
                <Input
                  value={loadIdentifiers.hbl_mbl}
                  onChange={(e) => setLoadIdentifiers({...loadIdentifiers, hbl_mbl: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  placeholder="House/Master Bill of Lading"
                  required
                  data-testid="hbl-mbl-input"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Ramp Pickup Number</Label>
                <Input
                  value={loadIdentifiers.pickup_number}
                  onChange={(e) => setLoadIdentifiers({...loadIdentifiers, pickup_number: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  placeholder="Optional"
                  data-testid="pickup-number-input"
                />
              </div>
            </div>
          </div>
          
          {/* LOAD QUANTITY SELECTION */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6">
            <h3 className="text-white font-bold text-lg mb-4 uppercase flex items-center gap-2">
              <Package className="text-[#FFA726]" size={20} />
              Load Quantity
            </h3>
            <RadioGroup 
              value={loadQuantity} 
              onValueChange={setLoadQuantity}
              className="flex flex-wrap gap-6"
              data-testid="load-quantity-radio"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Single" id="single" className="border-white/30" />
                <Label htmlFor="single" className="text-slate-300 font-semibold cursor-pointer">
                  Single Load (1 container)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Multiple" id="multiple" className="border-white/30" />
                <Label htmlFor="multiple" className="text-slate-300 font-semibold cursor-pointer">
                  Multiple Loads (up to {MAX_CONTAINERS})
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* CONTAINER ENTRIES */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg uppercase">Container Details</h3>
              {loadQuantity === 'Multiple' && containers.length < MAX_CONTAINERS && (
                <Button
                  type="button"
                  onClick={addContainer}
                  variant="outline"
                  size="sm"
                  className="border-[#00A6E8] text-[#00A6E8] hover:bg-[#00A6E8]/10"
                >
                  <Plus size={16} className="mr-1" /> Add Container
                </Button>
              )}
            </div>
            
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
              {containers.map((container, index) => (
                <div 
                  key={container.id} 
                  className="bg-[#1E293B]/50 border border-white/10 rounded-lg p-4"
                  data-testid={`container-entry-${index}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[#00A6E8] font-semibold text-sm">
                      Container {index + 1} of {containers.length}
                    </p>
                    {containers.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeContainer(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-8 px-2"
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <Label className="text-slate-400 text-xs mb-1 block">Container Number *</Label>
                      <Input
                        value={container.container_number}
                        onChange={(e) => updateContainer(index, 'container_number', e.target.value.toUpperCase())}
                        placeholder="ABCD1234567"
                        className={`bg-[#1E293B] border-white/10 text-white text-sm ${container.containerNumberError ? 'border-red-400' : ''}`}
                        required
                        data-testid={`container-number-${index}`}
                      />
                      {container.containerNumberError && (
                        <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                          <AlertTriangle size={12} /> {container.containerNumberError}
                        </p>
                      )}
                      <p className="text-slate-500 text-xs mt-1">ISO format: 4 letters + 7 digits</p>
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-xs mb-1 block">Size/Type *</Label>
                      <Select 
                        value={container.container_size} 
                        onValueChange={(v) => updateContainer(index, 'container_size', v)}
                      >
                        <SelectTrigger className="bg-[#1E293B] border-white/10 text-white text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-[200px]">
                          {CONTAINER_SIZES.map((size) => (
                            <SelectItem key={size} value={size}>{size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-slate-400 text-xs mb-1 block">Weight (lbs) *</Label>
                      <Input
                        type="number"
                        value={container.weight_lbs}
                        onChange={(e) => updateContainer(index, 'weight_lbs', e.target.value)}
                        placeholder="40000"
                        className="bg-[#1E293B] border-white/10 text-white text-sm"
                        required
                        data-testid={`container-weight-${index}`}
                      />
                      {parseInt(container.weight_lbs) >= OVERWEIGHT_THRESHOLD && (
                        <p className="text-yellow-400 text-xs mt-1 flex items-center gap-1">
                          <AlertTriangle size={12} /> Overweight (+$175)
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={container.hazmat}
                        onCheckedChange={(checked) => updateContainer(index, 'hazmat', checked)}
                        className="border-white/30"
                      />
                      <span className="text-slate-300 text-sm">Hazmat</span>
                      {container.hazmat && (
                        <span className="text-yellow-400 text-xs">(+$175)</span>
                      )}
                    </label>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={container.chassis_required}
                        onCheckedChange={(checked) => updateContainer(index, 'chassis_required', checked)}
                        className="border-white/30"
                      />
                      <span className="text-slate-300 text-sm">Chassis Required</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SHIPMENT DETAILS */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-6">
            <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
              <FileText className="text-[#00A6E8]" size={20} />
              Shipment Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-slate-300">Commodity *</Label>
                <Input
                  value={commonDetails.commodity}
                  onChange={(e) => setCommonDetails({...commonDetails, commodity: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  required
                  data-testid="commodity-input"
                />
              </div>
              
              <div>
                <Label className="text-slate-300">Steamship Line (SSL) *</Label>
                <Select 
                  value={commonDetails.steamship_line} 
                  onValueChange={(v) => setCommonDetails({...commonDetails, steamship_line: v})}
                >
                  <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white" data-testid="ssl-select">
                    <SelectValue placeholder="Select SSL" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-[300px]">
                    {STEAMSHIP_LINES.map((ssl) => (
                      <SelectItem key={ssl} value={ssl}>{ssl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-slate-300">Rail Ramp *</Label>
                <Select 
                  value={commonDetails.rail_ramp} 
                  onValueChange={(v) => setCommonDetails({...commonDetails, rail_ramp: v})}
                >
                  <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white" data-testid="rail-ramp-select">
                    <SelectValue placeholder="Select Rail Ramp" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                    {RAIL_RAMPS.map((ramp) => (
                      <SelectItem key={ramp} value={ramp}>{ramp}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-slate-300">Empty Return Depot</Label>
                <Select 
                  value={commonDetails.empty_depot} 
                  onValueChange={(v) => setCommonDetails({...commonDetails, empty_depot: v})}
                >
                  <SelectTrigger className="mt-2 bg-[#1E293B] border-white/10 text-white" data-testid="depot-select">
                    <SelectValue placeholder="Select Depot" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-[300px]">
                    {EMPTY_DEPOTS.map((depot) => (
                      <SelectItem key={depot} value={depot}>{depot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {commonDetails.empty_depot === 'Other (Enter custom depot below)' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#FFA726]/5 border border-[#FFA726]/20 rounded-lg p-4">
                <div>
                  <Label className="text-slate-300">Custom Depot Name *</Label>
                  <Input
                    value={commonDetails.custom_depot_name}
                    onChange={(e) => setCommonDetails({...commonDetails, custom_depot_name: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Custom Depot Address *</Label>
                  <Input
                    value={commonDetails.custom_depot_address}
                    onChange={(e) => setCommonDetails({...commonDetails, custom_depot_address: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                  />
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-slate-300">Last Free Day</Label>
              <Input
                type="date"
                value={commonDetails.last_free_day}
                onChange={(e) => setCommonDetails({...commonDetails, last_free_day: e.target.value})}
                className="mt-2 bg-[#1E293B] border-white/10 text-white w-48"
                data-testid="lfd-input"
              />
            </div>
          </div>

          {/* CONSIGNEE & DELIVERY (Grouped for Import) */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-6">
            <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
              <MapPin className="text-[#00A6E8]" size={20} />
              Consignee & Delivery
            </h3>
            
            {/* Consignee Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Consignee Name</Label>
                <Input
                  value={consignee.name}
                  onChange={(e) => setConsignee({...consignee, name: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  placeholder="Company or individual name"
                  data-testid="consignee-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Phone</Label>
                <Input
                  value={consignee.phone}
                  onChange={(e) => setConsignee({...consignee, phone: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  placeholder="(555) 123-4567"
                  data-testid="consignee-phone-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={consignee.email}
                  onChange={(e) => setConsignee({...consignee, email: e.target.value})}
                  className="mt-2 bg-[#1E293B] border-white/10 text-white"
                  placeholder="contact@company.com"
                  data-testid="consignee-email-input"
                />
              </div>
            </div>
            
            {/* Delivery Address */}
            <div className="pt-4 border-t border-white/10">
              <Label className="text-slate-300 mb-3 block">Delivery Address *</Label>
              <AddressFields
                formData={deliveryAddress}
                onChange={handleAddressChange}
                required={true}
                testIdPrefix="delivery"
              />
            </div>
          </div>

          {/* APPOINTMENT SCHEDULING */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 space-y-6">
            <h3 className="text-white font-bold text-lg uppercase flex items-center gap-2">
              <Calendar className="text-[#FFA726]" size={20} />
              Appointment Scheduling
            </h3>
            
            <div>
              <Label className="text-slate-300 mb-2 block">Appointment Type *</Label>
              <RadioGroup 
                value={appointmentDetails.appointment_type} 
                onValueChange={(v) => setAppointmentDetails({...appointmentDetails, appointment_type: v})}
                className="flex flex-wrap gap-4"
                data-testid="appointment-type-radio"
              >
                {APPOINTMENT_TYPES.map((type) => (
                  <div key={type.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={type.value} id={type.value} className="border-white/30" />
                    <Label htmlFor={type.value} className="text-slate-300 cursor-pointer">{type.label}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            
            {/* Show drop/pickup dates for Drop & Pick */}
            {appointmentDetails.appointment_type === 'Drop & Pick' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#FFA726]/5 border border-[#FFA726]/20 rounded-lg p-4">
                <div>
                  <Label className="text-slate-300">Drop Date *</Label>
                  <Input
                    type="date"
                    value={appointmentDetails.drop_date}
                    onChange={(e) => setAppointmentDetails({...appointmentDetails, drop_date: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                    data-testid="drop-date-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Pickup Date *</Label>
                  <Input
                    type="date"
                    value={appointmentDetails.pickup_date}
                    onChange={(e) => setAppointmentDetails({...appointmentDetails, pickup_date: e.target.value})}
                    className="mt-2 bg-[#1E293B] border-white/10 text-white"
                    required
                    data-testid="pickup-date-input"
                  />
                </div>
                {appointmentDetails.drop_date && appointmentDetails.pickup_date && 
                 appointmentDetails.drop_date !== appointmentDetails.pickup_date && (
                  <div className="col-span-2">
                    <p className="text-orange-400 text-sm flex items-center gap-1">
                      <AlertTriangle size={14} /> Drop Charge ($50) will be applied due to different dates
                    </p>
                  </div>
                )}
              </div>
            )}
            
            <div>
              <Label className="text-slate-300">Delivery Appointment</Label>
              <Input
                type="datetime-local"
                value={appointmentDetails.delivery_appointment}
                onChange={(e) => setAppointmentDetails({...appointmentDetails, delivery_appointment: e.target.value})}
                className="mt-2 bg-[#1E293B] border-white/10 text-white w-64"
                data-testid="delivery-appointment-input"
              />
            </div>
          </div>

          {/* SPECIAL INSTRUCTIONS */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6">
            <Label className="text-slate-300">Special Instructions</Label>
            <Textarea
              value={commonDetails.special_instructions}
              onChange={(e) => setCommonDetails({...commonDetails, special_instructions: e.target.value})}
              className="mt-2 bg-[#1E293B] border-white/10 text-white"
              rows={3}
              data-testid="instructions-input"
            />
          </div>

          {/* DOCUMENT UPLOAD */}
          <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6">
            <h3 className="text-white font-bold text-lg mb-4 uppercase flex items-center gap-2">
              <Paperclip className="text-[#00A6E8]" size={20} />
              Documents (Optional)
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Upload supporting documents such as BOL, packing list, or delivery instructions. Max 5 files.
            </p>
            <DocumentUploadPending
              files={pendingDocuments}
              onChange={setPendingDocuments}
            />
          </div>

          {/* CHARGE BREAKDOWN */}
          {rateData && (
            <ChargeBreakdown
              containers={containers}
              lineHaul={rateData.line_haul}
              zone={rateData.zone}
              appointmentType={appointmentDetails.appointment_type}
              dropDate={appointmentDetails.drop_date}
              pickupDate={appointmentDetails.pickup_date}
              showDetails={showChargeDetails}
              onToggleDetails={() => setShowChargeDetails(!showChargeDetails)}
            />
          )}
          
          {calculatingRate && (
            <div className="bg-[#1E293B]/50 rounded-lg p-4 text-center">
              <p className="text-slate-400">Calculating rate...</p>
            </div>
          )}

          {/* SUBMIT BUTTON */}
          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-[#FFA726] text-[#0A0E1A] font-bold uppercase tracking-wider hover:bg-[#FFB74D] hover:scale-[1.02] transition-all py-6"
            data-testid="import-submit-btn"
          >
            {loading ? 'Submitting...' : rateData?.zone === 'C' ? 'Submit Rate Request' : 'Submit Import Load'}
          </Button>
        </form>
      </div>
    </div>
  );
}
